import { Map } from "immutable";
import _ from "lodash";
import { createSelector } from "reselect";

import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import { stringFromDate, stringFromEndDate } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { stringFromTimespan } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { uiStateSelectors } from "../../modules/uiState";
import { createFromJS } from "../../types";

const getMarkersById = state => state.getIn(["data", "calendarMarkers", "byId"]);

// Get all the selected markers
const getSelectedMarkers = createSelector(
  // inputs
  getMarkersById,
  uiStateSelectors.getSelectedMarkers,
  // calculate based on inputs
  (markers, selection) => selection.map(id => markers.get(id))
);

const getDenormalisedSelectedMarkers = createSelector(
  // inputs
  getSelectedMarkers,
  calendarSelectors.getCalendar,
  definitionSelectors.getColorMap,
  // calculate based on inputs
  (markers, calendar, colorMap) => {
    const result = markers.map(obj => {
      const recmap = Map(obj).withMutations(rm => {
        // Calculate the start and end dates!
        const start = obj.startDate;
        const duration = obj.duration;
        const startDate = _.isNil(start.timestamp)
          ? null
          : dateWithTimestamp(calendar, start.timestamp, start.precision);
        const startLabel = startDate ? stringFromDate(calendar, startDate) : null;
        if (startDate) {
          rm.set("startDate", Map(startDate).set("label", startLabel));
        }

        const endDate = startDate ? dateByAddingSpan(calendar, startDate, duration) : null;
        // TODO: Need to work out stringFromEndDate instead, taking account of 24:00 inclusivity
        const endLabel = endDate ? stringFromEndDate(calendar, endDate, startDate) : null;
        if (endDate) {
          rm.set("endDate", Map(endDate).set("label", endLabel));
        }

        const durationLabel = stringFromTimespan(duration);
        rm.set("duration", Map(duration).set("label", durationLabel));

        // Convert repeatUntil
        const repeatUntil = obj.repeatUntil;

        const repeatUntilDate = _.isNil(repeatUntil.timestamp)
          ? null
          : dateWithTimestamp(calendar, repeatUntil.timestamp, repeatUntil.precision);
        const repeatUntilLabel = repeatUntilDate ? stringFromDate(calendar, repeatUntilDate) : null;
        if (repeatUntilDate) {
          rm.set("repeatUntil", Map(repeatUntilDate).set("label", repeatUntilLabel));
        }
        // Convert color ref to swatchColor
        const foundColor = colorMap.get(obj.color);
        rm.set("swatchColor", foundColor ? foundColor.swatchColor : obj.color);
      });
      return createFromJS.InspectorMarkerRecord(recmap);
    });

    return result;
  }
);

const deleteNonMatchingValues = (first, second, primKeys, objKeys) => {
  let result = first;
  primKeys.forEach(k => {
    if (first.get(k) !== second.get(k)) {
      result = result.delete(k);
    }
  });
  objKeys.forEach(k => {
    if (!first.get(k).equals(second.get(k))) {
      result = result.delete(k);
    }
  });

  return result;
};

// Merge the selected markers into a single object of agreed values
const getInspectorMarkerData = createSelector(
  // fetch parameters for last calculate function
  getDenormalisedSelectedMarkers,
  // Calculate result
  markers => {
    const markerIds = markers.map(o => o.id);
    const primKeys = ["color", "label", "initials", "ongoing", "type"];
    const objKeys = ["duration", "startDate", "narrativePosition"];
    let markerData = markers.size ? markers.first() : createFromJS.InspectorMarkerRecord({});

    markers.forEach((o, index) => {
      // Ignore the first value
      if (index === 0) {
        return;
      }
      markerData = deleteNonMatchingValues(markerData, o, primKeys, objKeys);
      // TODO: Need to remove references that don't match for multiple selections
    });
    return {
      markerIds,
      markerData
    };
  }
);

// Get the list of available colors for the inspector dropdown
const getColorOptions = createSelector(
  // fetch parameters for last calculate function
  definitionSelectors.getColorMap,
  // Calculate result
  colors =>
    colors
      .map((color, _id) =>
        createFromJS.DropdownOptionRecord({
          label: color.label,
          value: color.id,
          color: color.swatchColor
        })
      )
      .toList()
);

export default {
  getInspectorMarkerData,
  getColorOptions
};
